(() => {
  const instances = new WeakMap();

  const setupPolly = (root) => {
    if (!root || instances.has(root)) return;

    const controller = new AbortController();
    const { signal } = controller;
    const observers = [];
    let soundEnabled = false;
    const introVideo = root.querySelector('.polly-intro-video');
    let introFinished = !introVideo || introVideo.ended || Boolean(introVideo.error);
    const afterIntroCallbacks = new Set();

    const finishIntro = () => {
      if (introFinished) return;
      introFinished = true;
      afterIntroCallbacks.forEach((callback) => callback());
      afterIntroCallbacks.clear();
    };

    if (introVideo && !introFinished) {
      introVideo.addEventListener('ended', finishIntro, { once: true, signal });
      introVideo.addEventListener('error', finishIntro, { once: true, signal });
    }

    const afterIntro = (callback) => {
      if (introFinished) {
        callback();
        return;
      }
      afterIntroCallbacks.add(callback);
    };

    const revealNavigation = () => {
      const headerGroup = document.getElementById('header-group');
      document.body?.classList.add('polly-navigation-visible');
      if (!headerGroup) return;
      headerGroup.inert = false;
      headerGroup.removeAttribute('aria-hidden');
    };

    const hideNavigation = () => {
      const headerGroup = document.getElementById('header-group');
      document.body?.classList.remove('polly-navigation-visible');
      if (!headerGroup) return;
      headerGroup.inert = true;
      headerGroup.setAttribute('aria-hidden', 'true');
    };

    const observe = (element, callback, options) => {
      if (!element || !('IntersectionObserver' in window)) return null;
      const observer = new IntersectionObserver(callback, options);
      observer.observe(element);
      observers.push(observer);
      return observer;
    };

    const isVisible = (element) => {
      const rect = element.getBoundingClientRect();
      return rect.right > 0 && rect.left < window.innerWidth && rect.bottom > 0 && rect.top < window.innerHeight;
    };

    const playVideo = (video, restart = false) => {
      if (!(video instanceof HTMLVideoElement)) return Promise.resolve();

      if (restart) {
        try {
          video.currentTime = 0;
        } catch (_) {}
      }

      video.muted = !soundEnabled;
      video.volume = 1;
      const request = video.play();
      return request && typeof request.catch === 'function' ? request.catch(() => {}) : Promise.resolve();
    };

    const initIntroAutoplay = () => {
      if (!introVideo) return;

      let playAttemptPending = false;
      const isMacOSSafari = /Macintosh/i.test(navigator.userAgent)
        && /Safari/i.test(navigator.userAgent)
        && !/(Chrome|Chromium|CriOS|Edg|OPR)/i.test(navigator.userAgent);
      root.classList.toggle('is-macos-safari', isMacOSSafari);
      introVideo.muted = true;
      introVideo.defaultMuted = true;
      introVideo.playsInline = true;
      introVideo.setAttribute('muted', '');
      introVideo.setAttribute('playsinline', '');
      introVideo.setAttribute('webkit-playsinline', '');

      const attemptPlay = () => {
        if (document.hidden || introVideo.ended || !introVideo.paused || playAttemptPending) return;

        playAttemptPending = true;
        const request = introVideo.play();
        if (!request || typeof request.then !== 'function') {
          playAttemptPending = false;
          return;
        }

        request.then(() => {
          playAttemptPending = false;
          root.classList.remove('is-intro-autoplay-blocked');
        }).catch(() => {
          playAttemptPending = false;
          root.classList.add('is-intro-autoplay-blocked');
          revealNavigation();
        });
      };

      introVideo.addEventListener('loadeddata', attemptPlay, { signal });
      introVideo.addEventListener('canplay', attemptPlay, { signal });
      introVideo.addEventListener('playing', () => root.classList.remove('is-intro-autoplay-blocked'), { signal });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) attemptPlay();
      }, { signal });
      window.addEventListener('pageshow', attemptPlay, { signal });
      attemptPlay();
    };

    const initIntroLoading = () => {
      const container = root.querySelector('.polly-vide');
      const video = container?.querySelector('.polly-intro-video');
      const statusText = container?.querySelector('.polly-intro-status__text');
      if (!container || !video) {
        container?.classList.remove('is-loading');
        return;
      }

      let loadingTimer;
      let slowTimer;
      const setReady = () => {
        window.clearTimeout(loadingTimer);
        loadingTimer = null;
        window.clearTimeout(slowTimer);
        container.classList.remove('is-loading', 'is-slow', 'has-error');
      };
      const setLoading = () => {
        if (video.ended) return;
        container.classList.remove('has-error');
        if (statusText) statusText.textContent = 'Loading video…';
        if (container.classList.contains('is-loading') || loadingTimer) return;
        loadingTimer = window.setTimeout(() => {
          loadingTimer = null;
          if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
            container.classList.add('is-loading');
            revealNavigation();
          }
        }, 1500);
      };
      const setError = () => {
        window.clearTimeout(loadingTimer);
        loadingTimer = null;
        window.clearTimeout(slowTimer);
        container.classList.add('is-loading', 'has-error');
        if (statusText) statusText.textContent = 'Video failed to load. Please refresh and try again.';
        revealNavigation();
      };

      video.addEventListener('loadstart', setLoading, { signal });
      video.addEventListener('waiting', setLoading, { signal });
      video.addEventListener('stalled', setLoading, { signal });
      video.addEventListener('loadeddata', setReady, { signal });
      video.addEventListener('canplay', setReady, { signal });
      video.addEventListener('playing', setReady, { signal });
      video.addEventListener('error', setError, { signal });

      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) setReady();
      else setLoading();

      slowTimer = window.setTimeout(() => {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return;
        container.classList.add('is-slow');
        if (statusText) statusText.textContent = 'The video is loading slowly. Please wait…';
        revealNavigation();
      }, 5000);
      signal.addEventListener('abort', () => {
        window.clearTimeout(loadingTimer);
        window.clearTimeout(slowTimer);
      }, { once: true });
    };

    const initIntroScrollRelease = () => {
      if (!introVideo || introFinished) return;

      const releaseNextVideo = () => {
        if (!introFinished && window.scrollY > 24) finishIntro();
      };

      window.addEventListener('scroll', releaseNextVideo, { passive: true, signal });
      releaseNextVideo();
    };

    const initNavigationGate = () => {
      const body = document.body;
      const headerGroup = document.getElementById('header-group');
      if (!body || !headerGroup) return;

      let lastScrollY = window.scrollY;
      let hasScrolledDown = window.scrollY > 24;
      let furthestScrollY = window.scrollY;

      const hideWhilePlaying = () => {
        if (!introVideo || introVideo.ended || introVideo.error || window.scrollY > 24) return;
        hideNavigation();
      };

      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        furthestScrollY = Math.max(furthestScrollY, currentScrollY);
        if (furthestScrollY > 24) hasScrolledDown = true;
        if (hasScrolledDown && currentScrollY < lastScrollY && currentScrollY <= furthestScrollY - 8) revealNavigation();
        lastScrollY = currentScrollY;
      };

      body.classList.add('polly-navigation-gated');
      revealNavigation();
      window.addEventListener('scroll', handleScroll, { passive: true, signal });

      if (!introVideo || introVideo.ended) {
        revealNavigation();
        return;
      }

      introVideo.addEventListener('playing', hideWhilePlaying, { signal });
      introVideo.addEventListener('waiting', revealNavigation, { signal });
      introVideo.addEventListener('stalled', revealNavigation, { signal });
      introVideo.addEventListener('ended', revealNavigation, { once: true, signal });
      introVideo.addEventListener('error', revealNavigation, { once: true, signal });

      if (!introVideo.paused && introVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) hideWhilePlaying();
    };

    const initLazyVideos = () => {
      const videos = [...root.querySelectorAll('video.polly-lazy-video')];
      if (!videos.length) return;

      const prepare = (video) => {
        if (video.dataset.pollyPrepared === 'true') return;
        video.dataset.pollyPrepared = 'true';
        video.preload = 'metadata';
        video.load();
      };

      if (!('IntersectionObserver' in window)) {
        videos.forEach(prepare);
        return;
      }

      const preloadObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          prepare(entry.target);
          preloadObserver.unobserve(entry.target);
        });
      }, { rootMargin: '400px 0px', threshold: 0.01 });

      const playbackObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.15) {
            prepare(video);
            playVideo(video);
          } else {
            video.pause();
          }
        });
      }, { threshold: [0, 0.15] });

      videos.forEach((video) => {
        preloadObserver.observe(video);
        playbackObserver.observe(video);
      });
      observers.push(preloadObserver, playbackObserver);
    };

    const initVolume = () => {
      const button = root.querySelector('.polly-volume-toggle');
      const anchor = root.querySelector('[data-volume-anchor]');
      if (!button || !anchor) return;

      const syncButton = () => {
        button.classList.toggle('is-sound-on', soundEnabled);
        button.setAttribute('aria-pressed', String(soundEnabled));
        button.setAttribute('aria-label', soundEnabled ? 'Turn sound off' : 'Turn sound on');
      };

      const syncPosition = () => {
        const offset = window.innerWidth < 750 ? 18 : 45;
        const naturalTop = anchor.getBoundingClientRect().top + offset;
        button.style.top = `${Math.max(offset, naturalTop)}px`;
      };

      button.addEventListener('click', (event) => {
        event.stopPropagation();
        soundEnabled = !soundEnabled;
        root.querySelectorAll('video:not(.polly-intro-video)').forEach((video) => {
          video.muted = !soundEnabled;
          video.volume = 1;
        });

        if (soundEnabled) {
          root.querySelectorAll('.polly-video-component').forEach((component) => {
            if (!isVisible(component)) return;
            if (component.classList.contains('stories__card') && !component.classList.contains('is-active')) return;
            const active = component.querySelector('video.is-active') || component.querySelector('video');
            if (active?.paused) playVideo(active);
          });
        }

        syncButton();
      }, { signal });

      window.addEventListener('scroll', syncPosition, { passive: true, signal });
      window.addEventListener('resize', syncPosition, { passive: true, signal });
      syncButton();
      requestAnimationFrame(syncPosition);
    };

    const initShowcase = () => {
      const component = root.querySelector('.polly-showcase');
      const video = component?.querySelector('video');
      const control = component?.querySelector('.polly-video-toggle');
      if (!component || !video || !control) return;

      let prepared = false;
      const prepare = () => {
        if (prepared) return;
        prepared = true;
        video.preload = 'auto';
        video.load();
      };

      const prepareAfterScroll = () => {
        if (window.scrollY <= 24) return;
        const bounds = component.getBoundingClientRect();
        if (bounds.top < window.innerHeight + 400 && bounds.bottom > -400) prepare();
      };

      window.addEventListener('scroll', prepareAfterScroll, { passive: true, signal });
      prepareAfterScroll();

      const sync = () => {
        const paused = video.paused;
        component.classList.toggle('is-video-paused', paused);
        control.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
        control.setAttribute('aria-pressed', String(!paused));
      };

      const toggle = () => {
        if (video.paused) {
          prepare();
          playVideo(video).then(sync);
        } else {
          video.pause();
          sync();
        }
      };

      control.addEventListener('click', (event) => {
        event.stopPropagation();
        toggle();
      }, { signal });

      component.addEventListener('click', (event) => {
        if (!event.target.closest('.polly-video-toggle') && video.paused) playVideo(video).then(sync);
      }, { signal });

      component.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        toggle();
      }, { signal });

      video.addEventListener('play', sync, { signal });
      video.addEventListener('pause', sync, { signal });

      let resumeOnEnter = false;
      let inView = false;
      const startWhenAllowed = () => {
        if (!inView) return;
        if (resumeOnEnter || video.paused) {
          resumeOnEnter = false;
          prepare();
          playVideo(video).then(sync);
        }
      };

      afterIntro(startWhenAllowed);
      observe(component, ([entry]) => {
        inView = entry.isIntersecting && entry.intersectionRatio >= 0.15;
        if (!inView) {
          resumeOnEnter = !video.paused;
          video.pause();
          return;
        }

        if (introFinished) startWhenAllowed();
      }, { threshold: [0, 0.15] });

      sync();
    };

    const initTry = () => {
      const section = root.querySelector('.polly-try');
      const component = section?.querySelector('.try__watch');
      const trigger = component?.querySelector('.try__character-trigger');
      const start = component?.querySelector('.try__start');
      const videos = [...(component?.querySelectorAll('video.try__video') || [])];
      if (!section || !component || !trigger || !start || !videos.length) return;

      let activeIndex = 0;
      let inView = false;
      let switching = false;

      videos.forEach((video, index) => {
        video.classList.toggle('is-active', index === 0);
        video.loop = true;
        video.muted = true;
      });

      const playActive = (restart = false) => playVideo(videos[activeIndex], restart);
      const activate = (nextIndex) => {
        if (switching || nextIndex === activeIndex) return;
        switching = true;

        const previous = videos[activeIndex];
        const next = videos[nextIndex];
        next.muted = !soundEnabled;
        next.classList.add('is-active');

        playVideo(next, true).then(() => {
          previous.pause();
          previous.classList.remove('is-active');
          activeIndex = nextIndex;
          switching = false;
        });
      };

      trigger.addEventListener('click', () => {
        if (videos.length < 2) {
          playActive();
          return;
        }

        let nextIndex = activeIndex;
        while (nextIndex === activeIndex) nextIndex = Math.floor(Math.random() * videos.length);
        activate(nextIndex);
      }, { signal });

      start.addEventListener('click', (event) => {
        event.stopPropagation();
        playActive();
        component.classList.add('has-started');
      }, { signal });

      observe(section, ([entry]) => {
        inView = entry.isIntersecting && entry.intersectionRatio >= 0.15;
        if (inView) {
          playActive();
        } else {
          videos.forEach((video) => video.pause());
        }
      }, { threshold: [0, 0.15] });

      if (!inView) videos.forEach((video) => video.pause());
    };

    const initGallery = () => {
      const gallery = root.querySelector('.beampet-ai__gallery');
      const images = [...(gallery?.querySelectorAll('.beampet-ai__image') || [])];
      const dots = [...(gallery?.querySelectorAll('.beampet-ai__dot') || [])];
      if (!gallery || !images.length || images.length !== dots.length) return;

      let activeIndex = 0;
      let lastMouseX = null;

      const show = (index) => {
        activeIndex = (index + images.length) % images.length;
        images.forEach((image, imageIndex) => image.classList.toggle('is-active', imageIndex === activeIndex));
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === activeIndex;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-pressed', String(active));
        });
      };

      dots.forEach((dot, index) => dot.addEventListener('click', () => show(index), { signal }));

      gallery.addEventListener('click', (event) => {
        if (event.target.closest('.beampet-ai__dot')) return;
        show(activeIndex + 1);
      }, { signal });

      gallery.addEventListener('keydown', (event) => {
        if (event.target.closest('.beampet-ai__dot')) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          show(activeIndex - 1);
        } else if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          show(activeIndex + 1);
        }
      }, { signal });

      gallery.addEventListener('mouseenter', (event) => {
        lastMouseX = event.clientX;
      }, { signal });

      gallery.addEventListener('mousemove', (event) => {
        if (lastMouseX === null || event.target.closest('.beampet-ai__dot')) {
          lastMouseX = event.clientX;
          return;
        }

        const delta = event.clientX - lastMouseX;
        lastMouseX = event.clientX;
        if (!delta) return;

        const bounds = gallery.getBoundingClientRect();
        const x = Math.min(Math.max(event.clientX - bounds.left, 0), bounds.width - 1);
        const index = Math.floor(x / (bounds.width / images.length));
        if ((delta < 0 && index < activeIndex) || (delta > 0 && index > activeIndex)) show(index);
      }, { signal });

      gallery.addEventListener('mouseleave', () => {
        lastMouseX = null;
      }, { signal });

      show(0);
    };

    const initStories = () => {
      const stories = root.querySelector('[data-stories]');
      const cards = [...(stories?.querySelectorAll('.stories__card') || [])];
      const layers = [...(stories?.querySelectorAll('.stories__background') || [])];
      if (!stories || !cards.length || layers.length !== 2) return;

      let activeIndex = 0;
      let activeLayer = layers[0];
      let inView = false;

      const getVideo = (index) => cards[index]?.querySelector('video.stories__video');

      const setBackground = (index, immediate = false) => {
        const url = cards[index]?.dataset.background;
        if (!url) return;

        if (immediate) {
          activeLayer.style.backgroundImage = `url("${url}")`;
          activeLayer.classList.add('is-active');
          return;
        }

        const nextLayer = layers.find((layer) => layer !== activeLayer);
        nextLayer.style.backgroundImage = `url("${url}")`;
        requestAnimationFrame(() => {
          nextLayer.classList.add('is-active');
          activeLayer.classList.remove('is-active');
          activeLayer = nextLayer;
        });
      };

      const pauseAll = () => {
        cards.forEach((card) => card.querySelector('video')?.pause());
      };

      const playActive = (restart = false) => {
        pauseAll();
        return playVideo(getVideo(activeIndex), restart);
      };

      const update = (restart = true) => {
        cards.forEach((card, index) => {
          card.style.left = `${50 + (index - activeIndex) * 27.031}%`;
          card.classList.toggle('is-active', index === activeIndex);
        });
        setBackground(activeIndex);
        if (inView) playActive(restart);
      };

      cards.forEach((card, index) => {
        card.addEventListener('click', () => {
          if (index !== activeIndex) {
            activeIndex = index;
            update(true);
            return;
          }

          const video = getVideo(activeIndex);
          if (!video) return;
          if (video.paused) playActive(false);
          else video.pause();
        }, { signal });

        card.querySelector('.stories__start')?.addEventListener('click', (event) => {
          event.stopPropagation();
          playActive(false);
          stories.classList.add('has-started');
        }, { signal });
      });

      observe(stories, ([entry]) => {
        inView = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        if (inView) playActive(false);
        else pauseAll();
      }, { threshold: [0, 0.25] });

      setBackground(0, true);
      update(false);
    };

    const initPointer = () => {
      const pointer = root.querySelector('.polly-pointer');
      if (!pointer) return;

      const finePointer = window.matchMedia('(pointer: fine)');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const clickableSelector = [
        '.polly-clickable:not(.is-disabled):not([aria-disabled="true"])',
        '[data-polly-clickable]:not(.is-disabled):not([aria-disabled="true"])',
        'a[href]',
        'button:not(:disabled)',
        '[role="button"]:not([aria-disabled="true"])',
        'input:not(:disabled)',
        'select:not(:disabled)',
        'summary'
      ].join(', ');

      const applyMode = () => {
        root.classList.toggle('polly-pointer-active', finePointer.matches);
        document.body.classList.toggle('polly-custom-pointer-active', finePointer.matches);
      };
      const move = (event) => {
        if (!finePointer.matches) return;
        pointer.style.left = `${event.clientX}px`;
        pointer.style.top = `${event.clientY}px`;
        pointer.classList.toggle('is-action', Boolean(event.target.closest(clickableSelector)));
        pointer.classList.add('is-visible');
      };
      const release = () => pointer.classList.remove('is-pressed');
      const hide = () => {
        pointer.classList.remove('is-visible', 'is-action', 'is-pressed');
      };

      document.addEventListener('pointermove', move, { passive: true, signal });
      document.addEventListener('pointerleave', hide, { signal });
      document.addEventListener('pointerdown', () => {
        if (finePointer.matches && !reducedMotion.matches) pointer.classList.add('is-pressed');
      }, { signal });
      window.addEventListener('pointerup', release, { signal });
      window.addEventListener('pointercancel', release, { signal });
      window.addEventListener('blur', hide, { signal });

      if (typeof finePointer.addEventListener === 'function') {
        finePointer.addEventListener('change', applyMode, { signal });
      }
      applyMode();
    };

    const displayCards = root.querySelectorAll('.polly-display-card');
    if (displayCards.length) {
      const gap = displayCards.length > 1 ? Math.min(6.25, 12.5 / (displayCards.length - 1)) : 0;
      root.querySelector('.polly-display-list')?.style.setProperty('--polly-display-gap', `${gap}cqw`);
    }

    initIntroLoading();
    initIntroScrollRelease();
    initNavigationGate();
    initIntroAutoplay();
    initLazyVideos();
    initVolume();
    initShowcase();
    initTry();
    initGallery();
    initStories();
    initPointer();

    instances.set(root, {
      destroy() {
        controller.abort();
        observers.forEach((observer) => observer.disconnect());
        root.classList.remove('polly-pointer-active');
        document.body.classList.remove('polly-navigation-gated', 'polly-navigation-visible', 'polly-custom-pointer-active');
        const headerGroup = document.getElementById('header-group');
        if (headerGroup) {
          headerGroup.inert = false;
          headerGroup.removeAttribute('aria-hidden');
        }
        instances.delete(root);
      }
    });
  };

  const setupAll = (scope = document) => {
    if (scope.matches?.('[data-beambox-polly]')) setupPolly(scope);
    scope.querySelectorAll?.('[data-beambox-polly]').forEach(setupPolly);
  };

  setupAll();

  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-beambox-polly]')
      ? event.target
      : event.target.querySelector?.('[data-beambox-polly]');
    instances.get(root)?.destroy();
  });
})();
